import { useCallback, useState } from 'react';

export interface DateRangeFilter {
  startDate: string | null;
  endDate: string | null;
}

export interface FilterState {
  dateRange?: DateRangeFilter;
  multiSelect?: Record<string, string[]>;
}

const createDefaultFilters = (): FilterState => ({
  dateRange: { startDate: null, endDate: null },
  multiSelect: {},
});

export const useFilterState = () => {
  const [filters, setFilters] = useState<FilterState>(createDefaultFilters);

  const clearFilters = () => setFilters(createDefaultFilters());

  const removeFilter = (key: string, value?: string) => {
    setFilters((prev) => {
      if (key === 'dateRange') {
        return { ...prev, dateRange: { startDate: null, endDate: null } };
      }

      const multiSelect = { ...(prev.multiSelect || {}) };
      if (value) {
        const remaining = (multiSelect[key] || []).filter((entry) => entry !== value);
        if (remaining.length === 0) {
          delete multiSelect[key];
        } else {
          multiSelect[key] = remaining;
        }
      } else {
        delete multiSelect[key];
      }
      return { ...prev, multiSelect };
    });
  };

  const applyFilters = useCallback(
    <T>(data: T[], dateField?: keyof T): T[] => {
      return data.filter((item) => {
        const itemRecord = item as Record<string, unknown>;

        // Date range filtering
        if (dateField && filters.dateRange) {
          const { startDate, endDate } = filters.dateRange;
          if (startDate || endDate) {
            const rawValue = itemRecord[dateField as string];
            const itemDate = rawValue ? new Date(rawValue as string) : null;

            if (startDate) {
              const start = new Date(startDate);
              if (!itemDate || itemDate < start) {
                return false;
              }
            }

            if (endDate) {
              const end = new Date(endDate);
              if (!itemDate || itemDate > end) {
                return false;
              }
            }
          }
        }

        // Multi-select filters
        if (filters.multiSelect) {
          for (const [filterKey, selections] of Object.entries(filters.multiSelect)) {
            if (!selections || selections.length === 0) {
              continue;
            }

            const value = itemRecord[filterKey];
            const valueString =
              typeof value === 'boolean'
                ? String(value)
                : Array.isArray(value)
                  ? value
                  : value?.toString();

            if (Array.isArray(valueString)) {
              if (!valueString.some((entry) => selections.includes(String(entry)))) {
                return false;
              }
            } else if (!selections.includes(String(valueString))) {
              return false;
            }
          }
        }

        return true;
      });
    },
    [filters]
  );

  return {
    filters,
    setFilters,
    clearFilters,
    removeFilter,
    applyFilters,
  };
};
